import type { Catalogs, Translation } from "@amtana/i18n/catalog";

// The back office's words for the Providers page's Payments section (ADR-017). `ops-copy.ts` puts
// them together with the rest into the one catalogue the screens read.

export type PaymentCatalog = typeof EN;

const EN = {
  paymentSectionTitle: "Payments",
  paymentSectionDescription:
    "The providers people pay through. Paste each webhook address into the provider's dashboard.",
  paymentOn: "On",
  paymentOff: "Off",
  fieldPaymentEnabled: "New checkouts",
  fieldPaymentEnabledHint: "Off hides it from new checkouts. Its webhooks keep landing.",
  paymentEnabledOn: "Take new checkouts",
  paymentEnabledOff: "Take none",
  fieldApiBase: "API address",
  fieldApiKey: "API key",
  fieldWebhookSecret: "Webhook signing secret",
  fieldWebhookUrl: "Webhook address",
  fieldCapabilities: "Pays for",
  capabilityOneOff: "Credit packs",
  capabilitySubscription: "Plans",
  capabilitySeats: "Seats",
  capabilityOffSession: "Auto-recharge",
  capabilityPortal: "Customer portal",
  editPaymentTitle: "Edit payment provider",
  editPaymentDescription:
    "Leave a secret blank to keep it. A value you clear goes back to the environment's.",
  toastPaymentCheckOk: "The payment provider answered",
  toastPaymentCheckOkDescription: "It accepted the key in effect.",
  toastPaymentCheckFailed: "The payment provider did not answer as expected",
  checkPaymentRefused: "It refused the key.",
  checkNotConfigured: "An address, key or webhook secret is missing.",
  pricesCardTitle: "Prices",
  pricesCardDescription:
    "Every price billing charges. They live in code, and a change applies from the next checkout. Invoices keep what was paid, and a running Stripe subscription keeps its price.",
  pricesReadOnly: "Read-only",
  pricesPlans: "Plans",
  pricesAnalysisPacks: "Analysis credit packs",
  pricesAiPacks: "AI credit packs",
  pricesOffers: "Retention offers",
  pricesFreePlan: "Never charged",
  pricePlanLine: "{monthly} a month, {yearly} a year ({discount}% off)",
  priceSeatsLine: "{seats} seats included, {extra} each extra",
  priceSeatsUnlimited: "Seats without limit",
  priceOffSale: "Not on sale. Those already on it keep it, and staff can still set it.",
  priceFormerLine: "Subscriptions started before the rise keep {monthly} a month or {yearly} a year.",
  pricePackLabel: "{credits} credits",
  priceRecommended: "Recommended",
  priceFreeze: "Freeze",
  priceFreezeLine: "{price} a month",
  priceDiscount: "Discount",
  priceDiscountLine: "{percent}% off for {days} days",
  priceFreeDays: "Free days",
  priceFreeDaysLine: "{days} days",
  pricesRemoteAgent: "Remote agent",
  pricesProvisional: "Provisional",
  pricesProvisionalHint: "Provisional prices: Sergio sets the real ones before launch.",
  priceAddon: "The add-on",
  priceAddonLine: "{price} a month, {hours} awake hours included",
  priceHourPackLabel: "{hours} awake hours",
  priceModelTurn: "A turn on our model",
  priceModelTurnLine: "{input} AI credits per million tokens read, {output} per million written",
} as const;

const ES: Translation<PaymentCatalog> = {
  paymentSectionTitle: "Pagos",
  paymentSectionDescription:
    "Los proveedores con los que la gente paga. Pega cada dirección de webhook en el panel del proveedor.",
  paymentOn: "Activo",
  paymentOff: "Apagado",
  fieldPaymentEnabled: "Pagos nuevos",
  fieldPaymentEnabledHint: "Apagado, no se ofrece en pagos nuevos. Sus webhooks siguen llegando.",
  paymentEnabledOn: "Aceptar pagos nuevos",
  paymentEnabledOff: "No aceptar ninguno",
  fieldApiBase: "Dirección de la API",
  fieldApiKey: "Clave de la API",
  fieldWebhookSecret: "Secreto de firma del webhook",
  fieldWebhookUrl: "Dirección del webhook",
  fieldCapabilities: "Sirve para",
  capabilityOneOff: "Paquetes de créditos",
  capabilitySubscription: "Planes",
  capabilitySeats: "Puestos",
  capabilityOffSession: "Recarga automática",
  capabilityPortal: "Portal del cliente",
  editPaymentTitle: "Editar el proveedor de pagos",
  editPaymentDescription:
    "Deja un secreto en blanco para conservarlo. Un valor que borres vuelve al del entorno.",
  toastPaymentCheckOk: "El proveedor de pagos respondió",
  toastPaymentCheckOkDescription: "Aceptó la clave en uso.",
  toastPaymentCheckFailed: "El proveedor de pagos no respondió como se esperaba",
  checkPaymentRefused: "Rechazó la clave.",
  checkNotConfigured: "Falta una dirección, una clave o un secreto de webhook.",
  pricesCardTitle: "Precios",
  pricesCardDescription:
    "Todos los precios que cobra la facturación. Están en el código y un cambio vale desde el próximo pago. Las facturas conservan lo que se pagó, y una suscripción de Stripe en curso conserva su precio.",
  pricesReadOnly: "Solo lectura",
  pricesPlans: "Planes",
  pricesAnalysisPacks: "Paquetes de créditos de análisis",
  pricesAiPacks: "Paquetes de créditos de IA",
  pricesOffers: "Ofertas de retención",
  pricesFreePlan: "Nunca se cobra",
  pricePlanLine: "{monthly} al mes, {yearly} al año ({discount} % menos)",
  priceSeatsLine: "{seats} puestos incluidos, {extra} cada puesto extra",
  priceSeatsUnlimited: "Puestos sin límite",
  priceOffSale: "No está a la venta. Quien ya lo tiene lo conserva, y el equipo aún puede asignarlo.",
  priceFormerLine: "Las suscripciones anteriores a la subida conservan {monthly} al mes o {yearly} al año.",
  pricePackLabel: "{credits} créditos",
  priceRecommended: "Recomendado",
  priceFreeze: "Congelar",
  priceFreezeLine: "{price} al mes",
  priceDiscount: "Descuento",
  priceDiscountLine: "{percent} % menos durante {days} días",
  priceFreeDays: "Días gratis",
  priceFreeDaysLine: "{days} días",
  pricesRemoteAgent: "Agente remoto",
  pricesProvisional: "Provisional",
  pricesProvisionalHint: "Precios provisionales: Sergio fija los reales antes del lanzamiento.",
  priceAddon: "El complemento",
  priceAddonLine: "{price} al mes, {hours} horas encendida incluidas",
  priceHourPackLabel: "{hours} horas encendida",
  priceModelTurn: "Un turno con nuestro modelo",
  priceModelTurnLine: "{input} créditos de IA por millón de tokens leídos, {output} por millón escritos",
};

export const PAYMENT_CATALOGS: Catalogs<PaymentCatalog> = { en: EN, es: ES };
